/**
 * Active Riders Report (Email List)
 * 
 * Shows all active riders with their associated users and emails
 * Features:
 * - Copy all emails to clipboard
 * - Copy coaches' emails to clipboard
 * - Export to CSV
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient, Rider, User } from '../../api/rest-client';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { parseError } from '../../utils/errorHandling';
import { formatRiderName } from '../../utils/riderDisplay';

interface RiderUserRow {
  riderName: string;
  userName: string;
  userEmail: string;
}

export default function ActiveRidersReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [rows, setRows] = useState<RiderUserRow[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [seasonFilter, setSeasonFilter] = useState<string>('');

  useEffect(() => {
    loadSeasonConfig();
  }, []);

  // Reload data when season filter changes
  useEffect(() => {
    if (seasonFilter) {
      loadData();
    }
  }, [seasonFilter]);

  const loadSeasonConfig = async () => {
    try {
      const seasonConfig = await restClient.getSeasonConfig();
      setAvailableSeasons(seasonConfig.availableSeasons);
      setSeasonFilter(seasonConfig.currentSeason);
    } catch (err) {
      const parsed = parseError(err);
      setError(parsed.message);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch riders admin data (includes active_riders and users_minimal)
      const data = await restClient.getRidersAdmin();
      
      const allRiders = data.active_riders || [];
      const usersData = data.users_minimal || [];

      // Filter riders by selected season - check both primary season and seasons_active array (OR logic)
      const activeRiders = allRiders.filter((rider: Rider) => {
        // Check if rider is active in selected season
        // Either primary season matches OR seasons_active contains selected season
        return rider.season === seasonFilter || 
          (rider.seasons_active && rider.seasons_active.includes(seasonFilter));
      });

      setRiders(activeRiders);
      setUsers(usersData);

      // Build rows for table
      const tableRows: RiderUserRow[] = [];
      
      activeRiders.forEach(rider => {
        const associatedUsers = getAssociatedUsersForRider(rider, usersData);
        
        if (associatedUsers.length === 0) {
          tableRows.push({
            riderName: formatRiderName(rider),
            userName: 'No associated user',
            userEmail: 'N/A',
          });
        } else {
          associatedUsers.forEach(user => {
            tableRows.push({
              riderName: formatRiderName(rider),
              userName: user.name || user.email || 'Unknown User',
              userEmail: user.email || 'N/A',
            });
          });
        }
      });

      setRows(tableRows);
    } catch (err) {
      const parsed = parseError(err);
      setError(parsed.message);
    } finally {
      setLoading(false);
    }
  };

  const getAssociatedUsersForRider = (rider: Rider, allUsers: User[]): User[] => {
    if (!rider.associatedUsers) {
      return [];
    }

    try {
      const associations = JSON.parse(rider.associatedUsers);
      if (!associations || associations.length === 0) {
        return [];
      }

      return associations
        .map((assoc: any) => {
          return allUsers.find(u => u.username === assoc.user_id || u.email === assoc.user_id);
        })
        .filter((u: User | undefined): u is User => u !== undefined);
    } catch (e) {
      return [];
    }
  };

  const copyAllEmails = useCallback(() => {
    const emails = new Set<string>();
    
    // Use rows which already contains filtered data
    rows.forEach(row => {
      if (row.userEmail && row.userEmail !== 'N/A') {
        emails.add(row.userEmail);
      }
    });

    const emailList = Array.from(emails).join(', ');
    
    if (emailList) {
      navigator.clipboard.writeText(emailList).then(() => {
        alert(`${emails.size} email addresses copied to clipboard`);
      }).catch(() => {
        alert('Failed to copy to clipboard');
      });
    } else {
      alert('No email addresses found');
    }
  }, [rows]);

  const copyCoachesEmails = useCallback(() => {
    const emails = new Set<string>();
    
    // Filter for coaches only from the filtered riders (not all riders)
    const coachRiders = riders.filter(r => r.type === 'Coach');
    
    coachRiders.forEach(rider => {
      const associatedUsers = getAssociatedUsersForRider(rider, users);
      associatedUsers.forEach(user => {
        if (user.email) {
          emails.add(user.email);
        }
      });
    });

    const emailList = Array.from(emails).join(', ');
    
    if (emailList) {
      navigator.clipboard.writeText(emailList).then(() => {
        alert(`${emails.size} coach email addresses copied to clipboard`);
      }).catch(() => {
        alert('Failed to copy to clipboard');
      });
    } else {
      alert('No coach email addresses found');
    }
  }, [riders, users]);

  const downloadCSV = useCallback(() => {
    if (rows.length === 0) {
      alert('No data available to download');
      return;
    }

    // Prepare CSV data
    const csvData = [
      ['Rider Name', 'User', 'Email'], // Header
      ...rows.map(row => [row.riderName, row.userName, row.userEmail])
    ];

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => {
        const escaped = String(field).replace(/"/g, '""');
        return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
      }).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `active-riders-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary-900">Active Riders Report</h1>
              <p className="text-primary-600 mt-1">Email list of active riders and associated users</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => navigate('/admin/reports')}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Reports
              </Button>
              <Button
                onClick={copyAllEmails}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy All Emails
              </Button>
              <Button
                onClick={copyCoachesEmails}
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Coaches' Emails
              </Button>
              <Button
                onClick={downloadCSV}
                className="bg-green-500 hover:bg-green-600"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Season Filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center gap-4">
            <label htmlFor="season-filter" className="text-sm font-medium text-gray-700">
              Filter by Season:
            </label>
            <select
              id="season-filter"
              value={seasonFilter}
              onChange={(e) => setSeasonFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {availableSeasons.map((season) => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600">
              Showing {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left p-4 font-semibold text-sm text-gray-700 uppercase tracking-wider">
                    Rider Name
                  </th>
                  <th className="text-left p-4 font-semibold text-sm text-gray-700 uppercase tracking-wider">
                    User
                  </th>
                  <th className="text-left p-4 font-semibold text-sm text-gray-700 uppercase tracking-wider">
                    Email
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-gray-600">
                      No active riders found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-4 text-gray-900">{row.riderName}</td>
                      <td className="p-4 text-gray-700">
                        {row.userName === 'No associated user' ? (
                          <em className="text-gray-500">{row.userName}</em>
                        ) : (
                          row.userName
                        )}
                      </td>
                      <td className="p-4 text-gray-700">
                        {row.userEmail === 'N/A' ? (
                          <em className="text-gray-500">{row.userEmail}</em>
                        ) : (
                          row.userEmail
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
    </div>
  );
}
